import { FunctionComponent, useMemo, useState } from 'react';
import Button from 'components/UI/V3/Button/Button';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import styles from './EditCommissionModal.module.scss';
import { SaleCommissionsReport } from '@spike/commission-model';
import PriceField from 'components/UI/V3/Forms/PriceField';

export interface EditCommissionModalProps {
    sale: SaleCommissionsReport | null;
    onClose: () => void;
    onSave: (commissionAmount: string) => Promise<void>;
}

const EditCommissionModalContent: FunctionComponent<{
    sale: SaleCommissionsReport;
    onClose: () => void;
    onSave: (commissionAmount: string) => Promise<void>;
}> = ({ sale, onClose, onSave }) => {
    const [commissionValue, setCommissionValue] = useState(() =>
        Number.isFinite(Number(sale.commissionAmount)) ? Number(sale.commissionAmount) : 0
    );
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const validation = useMemo(() => {
        if (!Number.isFinite(commissionValue) || commissionValue < 0) {
            return { valid: false, error: 'Amount must be 0 or greater.' };
        }
        return { valid: true, value: commissionValue };
    }, [commissionValue]);
    const showError = touched && !validation.valid;
    const saveDisabled = saving || !validation.valid;

    const handleSave = async () => {
        setTouched(true);
        if (!validation.valid) {
            return;
        }
        setSaving(true);
        try {
            const normalized = validation.value!.toFixed(2);
            await onSave(normalized);
            onClose();
        } catch {
            // Caller shows toast; keep modal open and preserve input.
        } finally {
            setSaving(false);
        }
    };

    const handleEnterPressed = () => {
        if (!saveDisabled) {
            void handleSave();
        }
    };

    return (
        <GenericModal
            onClose={onClose}
            footer={
                <div className={styles.footer}>
                    <div className={styles.left}>
                        <Button
                            size="large"
                            className={styles.button}
                            label="Cancel"
                            onClick={onClose}
                            variant="black"
                            disabled={saving}
                        />
                    </div>
                    <div className={styles.left}>
                        <Button
                            label={saving ? 'Saving...' : 'Save'}
                            size="large"
                            className={styles.button}
                            onClick={() => void handleSave()}
                            variant="green"
                            isLoading={saving}
                            disabled={saveDisabled}
                        />
                    </div>
                </div>
            }
            className={styles.modal}
        >
            <div className={styles.content}>
                <span className={styles.title}>Edit Commission</span>
                <PriceField
                    name="commissionAmount"
                    label="Commission"
                    scale={2}
                    value={commissionValue}
                    onChange={(value: number | undefined) => {
                        setCommissionValue(Number.isFinite(Number(value)) ? Number(value) : 0);
                        setTouched(true);
                    }}
                    onBlur={() => setTouched(true)}
                    onEnterPressed={handleEnterPressed}
                    placeholder="0.00"
                    className={styles.priceInput}
                    disabled={saving}
                    max={999999.99}
                    precision={6}
                    autoFocus={false}
                    errors={
                        showError ? [{ fieldName: 'commissionAmount', errorMessage: validation.error! }] : undefined
                    }
                />
            </div>
        </GenericModal>
    );
};

export const EditCommissionModal: FunctionComponent<EditCommissionModalProps> = ({ sale, onClose, onSave }) => {
    if (!sale) {
        return null;
    }

    return (
        <EditCommissionModalContent
            key={sale.id}
            sale={sale}
            onClose={onClose}
            onSave={onSave}
        />
    );
};

export default EditCommissionModal;
